import React, { useState, useMemo, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme, Snackbar } from '@mui/material';
import AuthHandler from './components/AuthHandler';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import MapPage from './pages/MapPage';
import TransactionsPage from './pages/TransactionsPage';
import ProfilePage from './pages/ProfilePage';
import RewardsPage from './pages/RewardsPage';
import { useAppSelector, useAppDispatch } from './hooks/redux';
import { clearMessage } from './store/messageSlice';

function App() {
  const dispatch = useAppDispatch();
  const message = useAppSelector((state) => state.message.message);
  const color = useAppSelector((state) => state.message.color);
  
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode === 'dark' || savedMode === 'light') ? savedMode : 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: mode,
          primary: {
            main: '#1976d2',
          },
          secondary: {
            main: '#dc004e',
          },
        },
      }),
    [mode]
  );

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleCloseSnackbar = () => {
    dispatch(clearMessage());
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          {/* Public route for authentication */}
          <Route path="/" element={<AuthHandler />} />

          {/* Protected routes with layout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout toggleTheme={toggleTheme} mode={mode} />}>
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/transactions" element={<TransactionsPage />} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/rewards" element={<RewardsPage />} />
            </Route>
          </Route>

          {/* Catch all - redirect to root */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {message && (
          <Snackbar
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={true}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            message={message}
            sx={{
              '& .MuiSnackbarContent-root': {
                backgroundColor: color,
                fontWeight: '600',
              },
            }}
          />
        )}
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
